import { Button, InputIcon } from '@platform/ui-library';
import {
  ChangeEvent,
  FormEvent,
  useEffect,
  useRef,
  useState
} from 'react';
import { ErrorMessage } from '@app/form_components';
import { useServices } from '@app/hooks';
import { LoginState, LoginPasswordState } from './login-state';

export type LoginPasswordFormProps = {
  onComplete: () => void;
  state: LoginPasswordState;
  setState: (values: LoginState) => void;
};

export function LoginPasswordForm({
  onComplete,
  state,
  setState
}: LoginPasswordFormProps) {
  const { userService } = useServices();
  const contentRef = useRef<HTMLFormElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const { password, username } = state;

  useEffect(() => {
    requestAnimationFrame(() => {
      contentRef.current?.querySelector('input')?.focus();
    });
  }, []);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!password || !username) {
      setErrorMessage('Enter username & password before proceeding.');
    }

    setIsLoading(true);
    try {
      const settings = await userService.getAuthenticationSettings(state);
      if (!settings.validUsernameAndPassword) {
        setErrorMessage('Invalid username or password.');
        return;
      }

      if (settings.isMfaEnabled && settings.isRemembered) {
        await userService.login({
          ...state,
          ...settings
        });
        onComplete();
      } else {
        setState({
          code: '',
          ...state,
          ...settings
        });
      }

      setIsLoading(false);
    } catch (error) {
      setErrorMessage((error instanceof Error) ? (
        error.message
      ) : 'An unknown error occurred');
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={onSubmit} ref={contentRef}>
      <div className="box header">
        <h2>Login</h2>
      </div>
      <div className="box content">
        <InputIcon
          label="Username"
          name="username"
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            setState({ ...state, username: e.target.value });
          }}
          placeholder='User ID'
          required
          value={username}
        />
        <InputIcon
          label="Password"
          name="password"
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            setState({ ...state, password: e.target.value });
          }}
          placeholder='*******'
          required
          type="password"
          value={password}
        />
        <ErrorMessage message={errorMessage} />
      </div>
      <div className="box footer no-border">
        <Button type="submit">
          {(isLoading) ? 'Loading...' : 'Next'}
        </Button>
      </div>
    </form>
  );
}
